/* eslint-disable no-unused-vars */
import { api } from "@/axios";
import Vue from "vue";
import { permissionsMiddleWare } from "../../../utils/permissionsMiddleWare";
import { encryptData, decryptData } from "@/utils/helpers";
import { getCache, setCache, deleteCacheByPrefix } from "@/utils/cacheDB";

const state = {
  services: [],
  charges: [],
  allCharges: [],
  details: {},
  chargeId: ""
};

const mutations = {
  set_services(state, val) {
    state.services = val;
  },
  clear_services(state) {
    state.services = [];
    localStorage.removeItem("allCharges");
  },
  set_charges(state, val) {
    state.charges = val;
  },
  set_allcharges(state, val) {
    state.allCharges = val;
    const encryptedCharges = encryptData(val, process.env.VUE_APP_SECRET_KEY);
    localStorage.setItem("allCharges", encryptedCharges);
  },
  set_details(state, val) {
    state.details = val;
    state.chargeId = val.id;
  }
};

const actions = {
  async listServices({ commit }, query) {
    if (!permissionsMiddleWare("List Services")) {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : List Services",
        { duration: 3000 }
      );
      return;
    }
    // Cache-first: data lives in IndexedDB until logout — no API call
    // when the cache is populated
    const cacheKey = `services:${query || "all"}`;
    const cachedServices = await getCache(cacheKey);
    if (cachedServices && cachedServices.length > 0) {
      commit("set_services", cachedServices);
      return;
    }
    commit("set_loading", true, { root: true });
    try {
      const list = await api.get("/service", {
        params: { isOther: query }
      });
      commit("set_services", list.data.result);
      await setCache(cacheKey, list.data.result);
    } catch (error) {
      // toast handled by the axios interceptor
    } finally {
      commit("set_loading", false, { root: true });
    }
  },
  async addServicesCharges({ commit }, payload) {
    try {
      if (permissionsMiddleWare("Add Charge Against Service")) {
        commit("set_loading", true, { root: true });
        const list = await api.post("/charge/addcharge", payload);
        await deleteCacheByPrefix("services:");
        commit("set_services", list.data.result);
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Add Charge Against Service",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async filterServices({ commit }, query) {
    try {
      commit("set_loading", true, { root: true });
      const list = await api.get(
        `/service?isOther=${query.other}&branchId=${query.branch}`
      );
      commit("set_services", list.data.result);
      commit("set_loading", false, { root: true });
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async addServices({ commit, dispatch }, payload) {
    try {
      if (permissionsMiddleWare("Create Service")) {
        commit("set_loading", true, { root: true });
        await api.post(`/service`, payload);
        await deleteCacheByPrefix("services:");
        commit("set_loading", false, { root: true });
        await dispatch("listServices", payload.isOther);
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Create Service",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async updateService({ commit, dispatch }, { id, payload, isOther }) {
    try {
      if (permissionsMiddleWare("Update Service")) {
        commit("set_loading", true, { root: true });
        await api.put(`/service/${id}`, payload);
        await deleteCacheByPrefix("services:");
        commit("set_loading", false, { root: true });
        await dispatch("listServices", isOther);
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Update Service",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async updateCharge({ commit, dispatch }, { id, payload, isOther }) {
    try {
      if (permissionsMiddleWare("Update Only Charge")) {
        commit("set_loading", true, { root: true });
        await api.put(`/charge/update_charge/${id}`, payload);
        await deleteCacheByPrefix("services:");
        commit("set_loading", false, { root: true });
        await dispatch("listServices", isOther);
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Update Only Charge",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async doctorCharges({ commit }) {
    if (permissionsMiddleWare("List All Charges")) {
      try {
        commit("set_loading", true, { root: true });
        const items = await api.get("/charge");
        commit("set_charges", items.data.result);
        commit("set_loading", false, { root: true });
      } catch (error) {
        commit("set_loading", false, { root: true });
      }
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : List All Charges",
        { duration: 3000 }
      );
      commit("set_loading", false, { root: true });
    }
  },
  async gettAllCharges({ commit }) {
    const cachedCharges = localStorage.getItem("allCharges");
    if (cachedCharges) {
      const decryptedCharges = decryptData(
        cachedCharges,
        process.env.VUE_APP_SECRET_KEY
      );
      if (decryptedCharges && decryptedCharges.length > 0) {
        commit("set_allcharges", decryptedCharges);
        return;
      }
    }
    try {
      if (permissionsMiddleWare("List All Charges")) {
        commit("set_loading", true, { root: true });
        const items = await api.get("/charge/all-charges");
        commit("set_allcharges", items.data.result);
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : List All Charges",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async filterDoctorChargesByBranch({ commit }, params) {
    try {
      if (permissionsMiddleWare("List Branch Charges")) {
        commit("set_loading", true, { root: true });
        const items = await api.get(
          `/charge?take=${params.take}&branchId=${params.branch}`
        );
        commit("set_charges", items.data.result);
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : List Branch Charges",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async editCharge({ commit, dispatch }, { id, payload }) {
    if (permissionsMiddleWare("Update Charge")) {
      commit("set_loading", true);
      await api.put(`/charge/${id}`, payload);
      await deleteCacheByPrefix("services:");
      await dispatch("doctorCharges");
      commit("set_loading", false);
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : Update Charge",
        { duration: 3000 }
      );
    }
  },
  async deleteServices({ commit, dispatch }, params) {
    try {
      if (permissionsMiddleWare("Delete Service")) {
        commit("set_loading", true, { root: true });
        await api.delete(`/service/${params.serviceId}/${params.branchId}`);
        await deleteCacheByPrefix("services:");
        dispatch("listServices", params.id);
        Vue.toasted.success("Service deleted succesfully").goAway("3000");
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Delete Service",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async serviceDetails({ commit }, id) {
    if (permissionsMiddleWare("View Service")) {
      commit("set_loading", true, { root: true });
      const response = await api.get(`/service/${id}`);
      commit("set_details", response.data.result);
      commit("set_loading", false, { root: true });
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : View Service",
        { duration: 3000 }
      );
    }
  },
  async chargeDetail({ commit }, id) {
    if (permissionsMiddleWare("View Charges")) {
      const response = await api.get(`/charge/${id}`);
      commit("set_details", response.data.result);
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : View Charges",
        { duration: 3000 }
      );
    }
  },
  async addCharges({ commit, dispatch }, payload) {
    try {
      if (permissionsMiddleWare("Add Charge Type")) {
        commit("set_loading", true, { root: true });
        await api.post("/charge/type", payload);
        await deleteCacheByPrefix("services:");
        commit("set_loading", false, { root: true });
        Vue.toasted.success("Charges added successfully").goAway("3000");
        await dispatch("doctorCharges");
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Add Charge Type",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async deleteCharge({ commit, dispatch }, id) {
    try {
      if (permissionsMiddleWare("Delete Charge")) {
        commit("set_loading", true);
        await api.delete(`/charge/${id}`);
        await deleteCacheByPrefix("services:");
        await dispatch("doctorCharges");
        commit("set_loading", false);
        Vue.toasted.success("Charge deleted successfully").goAway("3000");
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Delete Charge",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  }
};

const getters = {
  listItems: state => state.services,
  charges: state => state.charges,
  allCharges: state => state.allCharges,
  detailsService: state => state.details,
  chargeId: state => state.chargeId
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
